import React from 'react'
import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet-async'
import moment from 'moment'
import Spinner from '../../util/spinner-shim'
import { FormattedMessage } from 'react-intl'
import { useParams, useNavigate, useLocation } from 'react-router-dom'

import NavigationBar from '../partials/NavigationBar'
import Footer from '../partials/Footer'
import DateRangePicker from '../partials/ConfigurePageComponents/DateRangePicker'
import { useDateRangePicker } from '../partials/ConfigurePageComponents/useDateRangePicker'

import TelemetryPump from '../partials/TelemetryComponents/TelemetryPump'
import TelemetryPump2 from '../partials/TelemetryComponents/TelemetryPump2'
import TelemetryET from '../partials/TelemetryComponents/TelemetryET'
import TelemetryBinDicators from '../partials/TelemetryComponents/TelemetryBinDicators'
import TelemetryWindMachine from '../partials/TelemetryComponents/TelemetryWindMachine'
import TelemetryWindMachineDiagnostics from '../partials/TelemetryComponents/TelemetryWindMachineDiagnostics'
import TelemetryStats from '../partials/TelemetryComponents/TelemetryStats'
import TelemetryFlow from '../partials/TelemetryComponents/TelemetryFlow'
import TelemetryGeneric from '../partials/TelemetryComponents/TelemetryGeneric'

import AppDispatcher from '../../dispatchers/AppDispatcher'
import withAppStore from '../../util/withAppStore'

import styles from '../../../css/telemetry.module.css'

const TelemetryChooser = function TelemetryChooser (
  {
    device,
    periodStart,
    periodStop,
    type,
    sensorData,
    statsData,
    flowStats,
    flowReady,
    metricReady,
    statsReady,
    params,
    location,
  }
) {
  let returnCode
  switch (type) {
    case 'pump':
      returnCode = (
        <TelemetryPump
          device={device}
          periodStart={periodStart}
          periodStop={periodStop}
          sensorData={sensorData}
          metricReady={metricReady}
        />
      )
      break
    case 'pump2':
      returnCode = (
        <TelemetryPump2
          device={device}
          periodStart={periodStart}
          periodStop={periodStop}
          sensorData={sensorData}
          metricReady={metricReady}
        />
      )
      break
    case 'evapotranspiration':
      returnCode = (
        <TelemetryET
          periodStart={periodStart}
          periodStop={periodStop}
          sensorData={sensorData}
          metricReady={metricReady}
        />
      )
      break
    case 'binDicator':
      returnCode = (
        <TelemetryBinDicators
          device={device}
          periodStart={periodStart}
          periodStop={periodStop}
          sensorData={sensorData}
          metricReady={metricReady}
        />
      )
      break
    case 'windMachineDiagnostics':
      returnCode = (
        <TelemetryWindMachineDiagnostics
          device={device}
          periodStart={periodStart}
          periodStop={periodStop}
          sensorData={sensorData}
          metricReady={metricReady}
          params={params}
          location={location}
        />
      )
      break
    case 'windMachine':
    case 'coldAirDrain':
    case 'pumpFrostWater':
      returnCode = (
        <TelemetryWindMachine
          device={device}
          periodStart={periodStart}
          periodStop={periodStop}
          sensorData={sensorData}
          metricReady={metricReady}
          params={params}
          location={location}
        />
      )
      break
    case 'stats':
      returnCode = (
        <TelemetryStats
          device={device}
          periodStart={periodStart}
          periodStop={periodStop}
          statsData={statsData}
          statsReady={statsReady}
          params={params}
          location={location}
        />
      )
      break
    case 'flow':
      returnCode = (
        <TelemetryFlow
          device={device}
          periodStart={periodStart}
          periodStop={periodStop}
          flowStats={flowStats}
          flowReady={flowReady}
          params={params}
          location={location}
        />
      )
      break
    case 'generic':
      returnCode = (
        <TelemetryGeneric
          device={device}
          periodStart={periodStart}
          periodStop={periodStop}
          sensorData={sensorData}
          metricReady={metricReady}
          params={params}
          location={location}
        />
      )
      break
    default:
  }
  return returnCode
}

function Telemetry (props) {
  const { deviceID, type, customerID } = useParams()
  const navigate = useNavigate()
  const location = useLocation()

  // Extract query parameters from location
  const searchParams = new URLSearchParams(location.search)
  const period = parseInt(searchParams.get('period') || '1', 10)

  const defaultPeriodDays = Number.isNaN(period) ? (type === 'stats' ? 6 : 1) : period

  // Shared date range picker used across charts
  const dateRangePicker = useDateRangePicker(
    moment().startOf('day').subtract(defaultPeriodDays, 'days'),
    moment().endOf('day')
  )

  const {
    state: {
      dateBegin,
      dateEnd,
      dateType,
      dayPickerIsVisible,
      draftRange,
    },
    closeDayPicker,
    editDateRange,
    applyDraftRange,
    handleDayClick,
    resetToLast31Days,
  } = dateRangePicker

  const backToDevice = React.useCallback(() => {
    const {
      data: {
        history: {
          past,
        },
      },
    } = props

    let backPath = `/${customerID}/devices/${deviceID}`

    if (past.length > 1 && past[1].split('/')[4] === 'settings') {
      backPath += '/settings'
    }

    navigate(backPath)
  }, [customerID, deviceID, navigate, props.data?.history?.past])

  React.useEffect(() => {
    AppDispatcher.dispatch({ action: 'get-device', deviceID })
  }, [deviceID])

  const {
    auth: {
      logout,
    },
    data: {
      lookup,
      lookupReady,
      metric,
      metricReady,
      stats,
      statsReady,
      flowStats,
      flowReady,
    },
    pages: {
      Telemetry: title,
    },
    route: {
      path,
    },
  } = props

  let subject
  if (!lookupReady || !lookup[deviceID]) {
    subject = (
      <div className='loading'>
        <Spinner name='double-bounce' />
      </div>
    )
  } else {
    const arrayOf = (value) => (Array.isArray(value) ? value : [])
    const periodFormat = (date) => (date ? date.toISOString() : undefined)
    subject = (
      <div className={type === 'windMachineDiagnostics' ? `${styles.pageWidth} ${styles.windMachineDatePicker}` : styles.pageWidth}>
        <div className={styles.dateInputs}>
          <DateRangePicker
            dateBegin={dateBegin}
            dateEnd={dateEnd}
            dateType={dateType}
            dayPickerIsVisible={dayPickerIsVisible}
            draftRange={draftRange}
            onClose={closeDayPicker}
            onEditRange={editDateRange}
            onDayClick={handleDayClick}
            onApplyRange={applyDraftRange}
            onResetToLast31Days={resetToLast31Days}
            layout='both'
            buttonStyle={{ marginLeft: 0 }}
          />
        </div>
        <div className={styles.maxDate}>
          <FormattedMessage
            id='general.dateMax'
            defaultMessage='31 day maximum view range'
            description='31 Day Max label'
            tagName='p'
          />
        </div>
        <div>
          <TelemetryChooser
            device={lookup[deviceID]}
            periodStart={periodFormat(dateBegin)}
            periodStop={periodFormat(dateEnd)}
            type={type}
            sensorData={arrayOf(metric)}
            statsData={arrayOf(stats)}
            flowStats={flowStats}
            flowReady={flowReady}
            metricReady={metricReady}
            statsReady={statsReady}
            params={{ deviceID, type, customerID }}
            location={location}
          />
        </div>
      </div>
    )
  }

  const {
    physical: {
      deviceNumber = '',
    } = {},
  } = lookup[deviceID] || {}

  return (
    <div>
      <Helmet><title>{title}</title></Helmet>
      <NavigationBar
        page={path}
        backFunc={backToDevice}
        centerText={deviceNumber}
        logOut={() => logout(navigate)}
      />
      {subject}
      <Footer />
    </div>
  )
}

Telemetry.propTypes = {
  auth: PropTypes.object,
  data: PropTypes.object,
  route: PropTypes.object,
  pages: PropTypes.object,
}

export default withAppStore(Telemetry)
